
import { useCallback } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from '../services';
import type { UseSmartCopywritingState } from './useSmartCopywritingState';

export const useCopyLogic = (
    state: UseSmartCopywritingState,
    setState: React.Dispatch<React.SetStateAction<UseSmartCopywritingState>>,
    setLoading: (key: keyof UseSmartCopywritingState['loading'], value: boolean) => void
) => {
    const { message } = App.useApp();

    const generateCopy = useCallback(async (targetSites: string[] = ['US'], competitorTitles: string[] = []) => {
        if (!state.project) return null;

        const selectedAudiences = state.audiences.filter(a => a.selected);
        const selectedScenarios = state.scenarios.filter(s => s.selected);

        if (selectedAudiences.length === 0) {
            message.warning('请先选择目标人群');
            return null;
        }

        if (selectedScenarios.length === 0) {
            message.warning('请先选择使用场景');
            return null;
        }

        setLoading('generation', true);
        try {
            const result = await smartCopywritingAPI.generateCopy(state.project.id, {
                selling_points: state.project.selling_points,
                keywords_by_site: state.project.keywords_by_site,
                selected_audiences: selectedAudiences,
                selected_scenarios: selectedScenarios,
                brand_info: state.project.brand_info ? {
                    name: state.project.brand_info.name,
                    tone: state.project.brand_info.tone,
                    archetype: state.project.brand_info.archetype,
                } : undefined,
                target_sites: targetSites,
                competitor_titles: competitorTitles,
            });

            setState(prev => ({
                ...prev,
                copyResult: result.result,
                currentStep: 4,
            }));

            message.success('文案生成成功');
            return result.result;
        } catch (error) {
            message.error('文案生成失败');
            throw error;
        } finally {
            setLoading('generation', false);
        }
    }, [state.project, state.audiences, state.scenarios, message, setLoading, setState]);

    const regenerateSingleFABE = useCallback(async (sellingPoint: string) => {
        if (!state.project || !state.copyResult) return;

        const selectedAudiences = state.audiences.filter(a => a.selected);
        const selectedScenarios = state.scenarios.filter(s => s.selected);
        const keywords = Object.values(state.project.keywords_by_site || {}).join(', ');

        try {
            const { result: newResult } = await smartCopywritingAPI.regenerateSellingPoint(state.project.id, {
                selling_point: sellingPoint,
                selected_audiences: selectedAudiences,
                selected_scenarios: selectedScenarios,
                keywords,
            });

            setState(prev => {
                if (!prev.copyResult) return prev;

                const newFABEAnalysis = prev.copyResult.fabe_analysis.map(item =>
                    item.selling_point === sellingPoint
                        ? { ...item, fabe: newResult.fabe, script_used: newResult.script_used }
                        : item
                );

                const index = prev.copyResult.fabe_analysis.findIndex(fp => fp.selling_point === sellingPoint);
                const newBulletPoints = { ...prev.copyResult.bullet_points };

                if (index !== -1) {
                    Object.keys(newResult.bullet_point).forEach(site => {
                        if (newBulletPoints[site] && newBulletPoints[site].length > index) {
                            const updatedList = [...newBulletPoints[site]];
                            updatedList[index] = newResult.bullet_point[site];
                            newBulletPoints[site] = updatedList;
                        }
                    });
                }

                return {
                    ...prev,
                    copyResult: {
                        ...prev.copyResult,
                        fabe_analysis: newFABEAnalysis,
                        bullet_points: newBulletPoints
                    }
                };
            });

            message.success('卖点优化完成');
        } catch (error) {
            message.error('重写失败');
            console.error(error);
        }
    }, [state.project, state.copyResult, state.audiences, state.scenarios, message, setState]);

    return { generateCopy, regenerateSingleFABE };
};
